import { useFormState, useFormApi } from "informedv4";
import { useState, useCallback, useEffect } from "react";
import { filter, omit, size, orderBy, groupBy, forEach } from "lodash";
import {  FixedCommentEntries, FixedCommentSetFormModel, InternalFormState } from "../Types";
import { v4 as uuidv4 } from "uuid";
import { DropResult } from "react-beautiful-dnd";

export interface UseOrderedCommentsState {
    commentOrder: string[],
    comments: FixedCommentEntries,
    add: () => void,
    remove: (key: string) => void
    onDragEnd: (result: DropResult) => void;
    reset: (entries: FixedCommentEntries) => void;
}

function GetOrderedEntries(entries: FixedCommentEntries): string[] {
    return orderBy(entries, e => e.displayOrder).map(e => e.id);
}

const useOrderedComments = (initialEntries: FixedCommentEntries): UseOrderedCommentsState => {

    const [comments, setComments] = useState<FixedCommentEntries>(initialEntries);
    const [commentOrder, setCommentOrder] = useState<string[]>(GetOrderedEntries(initialEntries));
    const formApi = useFormApi();

    const { values } = useFormState();

    const reset = (entries: FixedCommentEntries) => {
        setCommentOrder(GetOrderedEntries(entries));
        setComments(entries);
    }

    const remove = (key: string) => {
        const newComments = filter(commentOrder, co => co != key);
        setCommentOrder(newComments);
        setComments(omit(comments, [key]));
    }

    const add = () => {
        var newComments = {
            ...comments
        };

        var newGuid = uuidv4();

        newComments[newGuid] = {
            comment: "",
            description: "",
            id: newGuid,
            disabled: false,
            displayOrder: size(comments),
            followOnCommentAvailable: false,
            followOnCommentRequired: false,
            followOnCommentAnnotation: ""
        };

        setCommentOrder([...commentOrder, newGuid]);

        setComments(newComments);
    };

    const onDragEnd = useCallback((result: DropResult) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const [removed] = commentOrder.splice(result.source.index, 1);
        commentOrder.splice(result.destination.index, 0, removed);

        setCommentOrder([...commentOrder]);
    }, [commentOrder]);


    return {
        commentOrder: commentOrder,
        add: add,
        comments: comments,
        onDragEnd: onDragEnd,
        remove: remove,
        reset: reset
    };
};

export default useOrderedComments;
